/**
 * ConfigTableContent — body renderer for MasterPatternView when type='CONFIG_TABLE'.
 *
 * Provides: toolbar (title + Acciones), company selector, config data loading,
 * global-values toggle, company-link confirmation dialog, and "Vincular compañías" modal.
 * Delegates body rendering to the consumer via `renderConfigBody(ctx)`.
 *
 * @internal
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Menu, ChevronDown, Globe, Link2, RotateCcw } from 'lucide-react';
import { useMasterViewStore } from '../store/MasterViewContext';
import { CompanySelector } from './toolbar/CompanySelector';
import { CompanyEnableModal } from './modals/CompanyEnableModal';
import type { CompanyEnableChange } from './modals/CompanyEnableModal';
import type { MasterPatternViewDefinition } from '../types/definition.types';
import type { MasterPatternViewCompany, ConfigTableService, ConfigSaveRow } from '../types/service.types';
import type { ConfigBodyContext } from '../types/props.types';

interface ConfigTableContentProps {
  definition: MasterPatternViewDefinition;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  configService?: ConfigTableService<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderConfigBody?: (ctx: ConfigBodyContext<any>) => React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderToolbarActions?: (ctx: ConfigBodyContext<any>) => React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  renderActionsMenuItems?: (ctx: ConfigBodyContext<any>) => React.ReactNode;
  title: string;
  companies?: MasterPatternViewCompany[];
  defaultCompany?: MasterPatternViewCompany;
  onCompanyChange?: (company: MasterPatternViewCompany) => void;
  toast?: (type: 'success' | 'error' | 'warning' | 'info', message: string) => void;
}

export function ConfigTableContent({
  definition,
  configService,
  renderConfigBody,
  renderToolbarActions,
  renderActionsMenuItems,
  title,
  companies = [],
  defaultCompany,
  onCompanyChange,
  toast,
}: ConfigTableContentProps) {
  const companyBehavior = definition.companyBehavior ?? 'NONE';

  const activeCompanyContext = useMasterViewStore((s) => s.activeCompanyContext);
  const setActiveCompanyContext = useMasterViewStore((s) => s.setActiveCompanyContext);
  const isShowGlobalValuesEnabled = useMasterViewStore((s) => s.isShowGlobalValuesEnabled);
  const toggleShowGlobalValues = useMasterViewStore((s) => s.toggleShowGlobalValues);

  const [rows, setRows] = useState<unknown[]>([]);
  const [globalData, setGlobalData] = useState<Map<string, unknown>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [linkedCompanyIds, setLinkedCompanyIds] = useState<Set<string>>(new Set());
  const [pendingCompany, setPendingCompany] = useState<MasterPatternViewCompany | null>(null);
  const [isLinking, setIsLinking] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
  const [isLinkModalSaving, setIsLinkModalSaving] = useState(false);

  const companyId = activeCompanyContext === 'global' ? undefined : (activeCompanyContext ?? undefined);
  const isCompanyMode = companyBehavior !== 'NONE' && !!activeCompanyContext && activeCompanyContext !== 'global';
  const showsGlobalOption = companyBehavior === 'GLOBAL';

  // Initialize company context on mount
  useEffect(() => {
    if (companyBehavior === 'COMPANY_SPECIFIC' && companies.length > 0) {
      const initial = defaultCompany ?? companies[0];
      setActiveCompanyContext(initial.id);
    } else if (companyBehavior === 'GLOBAL' && defaultCompany && defaultCompany.id !== 'global') {
      // Pre-select the provided company (e.g. when consumed inside an app that already has a company context)
      setActiveCompanyContext(defaultCompany.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load linked company IDs on mount (GLOBAL)
  useEffect(() => {
    if (companyBehavior === 'GLOBAL' && configService?.getLinkedCompanies) {
      void configService.getLinkedCompanies().then((ids) => setLinkedCompanyIds(new Set(ids))).catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load config data when company context changes
  useEffect(() => {
    if (!configService) return;
    setIsLoading(true);
    void configService.getConfigData(companyId)
      .then(({ rows: newRows }) => setRows(newRows))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompanyContext]);

  // Pre-load global data whenever in company+GLOBAL context so it's ready when the toggle is turned on
  useEffect(() => {
    if (!configService || !isCompanyMode || companyBehavior !== 'GLOBAL') {
      setGlobalData(new Map());
      return;
    }
    void configService.getConfigData(undefined)
      .then(({ rows: globalRows }) => {
        const map = new Map<string, unknown>();
        for (const row of globalRows ?? []) {
          const r = row as { rowId?: string };
          if (r.rowId) map.set(r.rowId, row);
        }
        setGlobalData(map);
      })
      .catch(() => setGlobalData(new Map()));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCompanyContext]);

  const handleCompanyChange = useCallback((company: MasterPatternViewCompany) => {
    if (company.id === 'global') {
      setActiveCompanyContext('global');
      return;
    }
    if (companyBehavior === 'GLOBAL' && !linkedCompanyIds.has(company.id)) {
      setPendingCompany(company);
    } else {
      setActiveCompanyContext(company.id);
      onCompanyChange?.(company);
    }
  }, [companyBehavior, linkedCompanyIds, setActiveCompanyContext, onCompanyChange]);

  const handleConfirmLink = useCallback(async () => {
    if (!pendingCompany || !configService?.linkCompany) return;
    setIsLinking(true);
    try {
      await configService.linkCompany(pendingCompany.id, rows);
      setActiveCompanyContext(pendingCompany.id);
      if (configService.getLinkedCompanies) {
        const updatedIds = await configService.getLinkedCompanies();
        setLinkedCompanyIds(new Set(updatedIds));
      }
      onCompanyChange?.(pendingCompany);
      setPendingCompany(null);
    } catch {
      toast?.('error', 'Error al vincular la compañía');
      setPendingCompany(null);
    } finally {
      setIsLinking(false);
    }
  }, [pendingCompany, configService, rows, setActiveCompanyContext, onCompanyChange, toast]);

  const handleSave = useCallback(async (changedRows: ConfigSaveRow<unknown>[]) => {
    if (!configService) return;
    setIsSaving(true);
    try {
      await configService.saveConfig(changedRows, companyId);
      const { rows: newRows } = await configService.getConfigData(companyId);
      setRows(newRows);
      toast?.('success', 'Guardado exitosamente');
    } catch {
      toast?.('error', 'Error al guardar');
    } finally {
      setIsSaving(false);
    }
  }, [configService, companyId, toast]);

  const handleRestoreRow = useCallback((rowId: string) => {
    const globalRow = globalData.get(rowId);
    if (!globalRow) return;
    setRows((prev) => prev.map((r) => {
      const row = r as { rowId?: string };
      return row.rowId === rowId ? globalRow : r;
    }));
  }, [globalData]);

  const handleRestoreAll = useCallback(() => {
    if (!globalData.size) return;
    setRows((prev) => prev.map((r) => {
      const row = r as { rowId?: string };
      if (!row.rowId) return r;
      return globalData.get(row.rowId) ?? r;
    }));
  }, [globalData]);

  const handleLinkModalConfirm = useCallback(async (changes: CompanyEnableChange[]) => {
    if (!configService?.updateCompanyLinks) return;
    const toLink = changes.filter((c) => c.action === 'enable').map((c) => c.companyId);
    const toUnlink = changes.filter((c) => c.action === 'disable').map((c) => c.companyId);
    setIsLinkModalSaving(true);
    try {
      await configService.updateCompanyLinks(toLink, toUnlink, rows);
      if (configService.getLinkedCompanies) {
        const updatedIds = await configService.getLinkedCompanies();
        setLinkedCompanyIds(new Set(updatedIds));
      }
      const { rows: newRows } = await configService.getConfigData(companyId);
      setRows(newRows);
      setLinkModalOpen(false);
    } catch {
      toast?.('error', 'Error al actualizar vínculos');
    } finally {
      setIsLinkModalSaving(false);
    }
  }, [configService, rows, companyId, toast]);

  const showMostrarDatosGlobales = companyBehavior === 'GLOBAL' && isCompanyMode;
  const showUsarDatosGlobales = companyBehavior === 'GLOBAL' && isCompanyMode && globalData.size > 0;
  const showVincularCompanias = companyBehavior === 'GLOBAL';
  const hasBuiltinActionsItems = showMostrarDatosGlobales || showVincularCompanias;
  const hasActionsItems = hasBuiltinActionsItems || !!renderActionsMenuItems;

  const ctx: ConfigBodyContext<unknown> = {
    rows,
    isCompanyMode,
    showGlobalValues: isShowGlobalValuesEnabled,
    globalData,
    isSaving,
    onSave: handleSave,
    onRestoreRow: handleRestoreRow,
    onRestoreAll: handleRestoreAll,
  };

  // Close dropdown when clicking outside
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!actionsDropdownOpen) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setActionsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [actionsDropdownOpen]);

  return (
    <div data-testid="master-pattern-view" className="relative flex flex-col h-screen bg-background-secondary dark:bg-dark-bg-primary overflow-hidden">
      {/* Header: Title | Acciones | renderToolbarActions */}
      <div className="flex items-center justify-between px-5 pt-3.5 pb-0">
        {title && (
          <h1 className="text-xl font-semibold text-[#1355b7] tracking-tight" data-testid="master-view-title">
            {title}
          </h1>
        )}

        {(hasActionsItems || renderToolbarActions) && (
          <div className="ml-auto flex items-center gap-2">
            {/* Acciones dropdown — left of any custom toolbar actions */}
            {hasActionsItems && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setActionsDropdownOpen((v) => !v)}
                  data-testid="toolbar-actions-dropdown-button"
                  className="inline-flex items-center gap-2 px-3 h-9 rounded-md border text-sm font-bold cursor-pointer transition-all bg-background-secondary dark:bg-dark-bg-secondary border-border-primary dark:border-dark-border-primary text-content-tertiary dark:text-dark-content-tertiary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary"
                >
                  <Menu size={14} />
                  Acciones
                  <ChevronDown size={12} className={`transition-transform ${actionsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {actionsDropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 bg-bg-primary dark:bg-dark-bg-primary rounded-md border border-border-primary dark:border-dark-border-primary shadow-md z-50 min-w-[250px] py-1">
                    {showUsarDatosGlobales && (
                      <button
                        type="button"
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary cursor-pointer"
                        onClick={() => { handleRestoreAll(); setActionsDropdownOpen(false); }}
                        data-testid="toolbar-use-global-data"
                      >
                        <RotateCcw size={13} />
                        Usar datos globales
                      </button>
                    )}

                    {showMostrarDatosGlobales && (
                      <button
                        type="button"
                        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-sm text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary cursor-pointer"
                        onClick={() => { toggleShowGlobalValues(); setActionsDropdownOpen(false); }}
                        data-testid="toolbar-toggle-global-values"
                      >
                        <span className="flex items-center gap-2 whitespace-nowrap">
                          <Globe size={13} />
                          Mostrar datos globales
                        </span>
                        <span style={{
                          width: 28, height: 16, borderRadius: 8, flexShrink: 0,
                          background: isShowGlobalValuesEnabled ? 'var(--text-color-primary-custom-600)' : 'var(--mpv-toggle-inactive)',
                          position: 'relative', display: 'inline-block',
                          transition: 'background .2s',
                        }}>
                          <span style={{
                            width: 12, height: 12, borderRadius: '50%', background: 'var(--color-bg-primary)',
                            position: 'absolute', top: 2,
                            left: isShowGlobalValuesEnabled ? 14 : 2,
                            transition: 'left .15s',
                            boxShadow: '0 1px 2px rgba(0,0,0,.2)',
                          }} />
                        </span>
                      </button>
                    )}

                    {(showMostrarDatosGlobales || showUsarDatosGlobales) && showVincularCompanias && (
                      <hr className="my-1 border-border-primary dark:border-dark-border-primary" />
                    )}

                    {showVincularCompanias && (
                      <button
                        type="button"
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-content-primary dark:text-dark-content-primary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary cursor-pointer"
                        onClick={() => { setLinkModalOpen(true); setActionsDropdownOpen(false); }}
                        data-testid="toolbar-link-companies"
                      >
                        <Link2 size={13} />
                        Habilitar compañías
                      </button>
                    )}

                    {/* Custom actions — always available, separated from built-in when both exist */}
                    {renderActionsMenuItems && (
                      <>
                        {hasBuiltinActionsItems && (
                          <hr className="my-1 border-border-primary dark:border-dark-border-primary" />
                        )}
                        {renderActionsMenuItems(ctx)}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Custom toolbar actions (e.g. Guardar) — right of Acciones */}
            {renderToolbarActions && renderToolbarActions(ctx)}
          </div>
        )}
      </div>

      {/* Company selector */}
      {companyBehavior !== 'NONE' && (
        <div className="px-5 pb-2 pt-2">
          <CompanySelector
            companies={companies}
            activeCompanyId={activeCompanyContext ?? undefined}
            showGlobalOption={showsGlobalOption}
            onCompanyChange={handleCompanyChange}
            toast={toast}
          />
        </div>
      )}

      {/* Config body */}
      <div className="flex-1 min-h-0 flex flex-col gap-2.5 px-5 pb-5 pt-3">
        <div className="bg-bg-primary dark:bg-dark-bg-primary rounded-xl border border-border-primary dark:border-dark-border-primary overflow-hidden flex flex-col flex-1 min-h-0">
          <div className="flex-1 min-h-0 overflow-auto flex flex-col">
            {isLoading ? (
              <div className="flex items-center justify-center p-8 text-sm text-content-tertiary dark:text-dark-content-tertiary">
                Cargando...
              </div>
            ) : (
              renderConfigBody?.(ctx)
            )}
          </div>
        </div>
      </div>

      {/* Link-company confirmation dialog */}
      {pendingCompany && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50">
          <div className="bg-bg-primary dark:bg-dark-bg-primary rounded-xl border border-border-primary dark:border-dark-border-primary shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-base font-semibold text-content-primary dark:text-dark-content-primary mb-2">
              Habilitar compañía
            </h3>
            <p className="text-sm text-content-secondary dark:text-dark-content-secondary mb-6">
              {`¿Deseas habilitar "${pendingCompany.name}" en esta configuración?`}
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { if (!isLinking) setPendingCompany(null); }}
                disabled={isLinking}
                className="px-4 py-2 text-sm font-medium rounded-md border border-border-primary dark:border-dark-border-primary text-content-secondary dark:text-dark-content-secondary hover:bg-background-secondary dark:hover:bg-dark-bg-secondary disabled:opacity-50 cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => void handleConfirmLink()}
                disabled={isLinking}
                className="px-4 py-2 text-sm font-medium rounded-md bg-[#0e79fd] text-white hover:bg-[#0c6be0] disabled:opacity-50 cursor-pointer"
              >
                {isLinking ? 'Habilitando...' : 'Habilitar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Habilitar compañías modal */}
      <CompanyEnableModal
        isOpen={linkModalOpen}
        recordId=""
        companies={companies}
        assignedCompanyIds={Array.from(linkedCompanyIds)}
        onClose={() => setLinkModalOpen(false)}
        onConfirm={(changes) => void handleLinkModalConfirm(changes)}
        isLoading={isLinkModalSaving}
      />
    </div>
  );
}

